/**
 * Admin Portal: roles and route access.
 *
 * Phase 0: Documents which sections are admin-only vs moderator-accessible.
 * Used by AdminPortalLayout and AdminPortalNav for route guards and UI hiding.
 *
 * Platform Admin (useAdmin): full access to all portal sections.
 * Moderator (useModerator, not admin): access to Overview, Usage, Accessibility,
 * Moderation, Audit; no User management, Listings control/schedule, or Config.
 */

export const ADMIN_PORTAL_PATH = '/admin'

export type AdminPortalSectionId =
  | 'overview'
  | 'usage'
  | 'accessibility'
  | 'moderation'
  | 'listings-health'
  | 'listings-report'
  | 'listings-management'
  | 'listings-channels'
  | 'language-control'
  | 'streaming-control'
  | 'streaming-report'
  | 'invites'
  | 'users'
  | 'announcements'
  | 'feature-flags'
  | 'audit'

export interface AdminPortalSection {
  id: AdminPortalSectionId
  label: string
  path: string
  /** If true, only platform admins see this link; moderators do not. */
  adminOnly: boolean
}

/** Portal sections for MVP. Order defines nav order. */
export const ADMIN_PORTAL_SECTIONS: AdminPortalSection[] = [
  { id: 'overview', label: 'Overview', path: `${ADMIN_PORTAL_PATH}`, adminOnly: false },
  { id: 'usage', label: 'Usage & growth', path: `${ADMIN_PORTAL_PATH}/usage`, adminOnly: false },
  { id: 'accessibility', label: 'Accessibility impact', path: `${ADMIN_PORTAL_PATH}/accessibility`, adminOnly: false },
  { id: 'moderation', label: 'Moderation', path: `${ADMIN_PORTAL_PATH}/moderation`, adminOnly: false },
  { id: 'listings-health', label: 'Listings health', path: `${ADMIN_PORTAL_PATH}/listings-health`, adminOnly: true },
  { id: 'listings-report', label: 'Listings report', path: `${ADMIN_PORTAL_PATH}/listings-report`, adminOnly: true },
  { id: 'listings-management', label: 'Listings management', path: `${ADMIN_PORTAL_PATH}/listings-management`, adminOnly: true },
  { id: 'listings-channels', label: 'Channel control', path: `${ADMIN_PORTAL_PATH}/listings-channels`, adminOnly: true },
  { id: 'language-control', label: 'Language control', path: `${ADMIN_PORTAL_PATH}/language-control`, adminOnly: true },
  { id: 'streaming-control', label: 'Streaming control', path: `${ADMIN_PORTAL_PATH}/streaming-control`, adminOnly: true },
  { id: 'streaming-report', label: 'Streaming report', path: `${ADMIN_PORTAL_PATH}/streaming-report`, adminOnly: true },
  { id: 'invites', label: 'Invites', path: `${ADMIN_PORTAL_PATH}/invites`, adminOnly: true },
  { id: 'users', label: 'User management', path: `${ADMIN_PORTAL_PATH}/users`, adminOnly: true },
  { id: 'announcements', label: 'Announcement management', path: `${ADMIN_PORTAL_PATH}/announcements`, adminOnly: true },
  { id: 'feature-flags', label: 'Feature flags', path: `${ADMIN_PORTAL_PATH}/feature-flags`, adminOnly: true },
  { id: 'audit', label: 'Audit', path: `${ADMIN_PORTAL_PATH}/audit`, adminOnly: false },
]

/** Sections visible to moderators (admin-only sections filtered out when not admin). */
export function getPortalSectionsForNav(isAdmin: boolean): AdminPortalSection[] {
  if (isAdmin) return ADMIN_PORTAL_SECTIONS
  return ADMIN_PORTAL_SECTIONS.filter((s) => !s.adminOnly)
}
